import { List, Map } from 'immutable';
import { reduce } from 'ramda';
import createReducer from './utils/createReducer';
import { useManipulator } from './player';
import { ActionTypes } from '../store/createStore';

export const MOUSE_CONTROLS = ['orbit', 'pan', 'zoom'];

/**
 * This module manages commands and tools of the player
 *
 * @module commands
 * @public
 */

const ADD_COMMAND = 'ADD_COMMAND';
const REMOVE_COMMAND = 'REMOVE_COMMAND';
const UPDATE_COMMAND = 'UPDATE_COMMAND';
const FAIL_COMMAND = 'FAIL_COMMAND';
const FINISH_COMMAND = 'FINISH_COMMAND';
const RUN_COMMAND = 'RUN_COMMAND';
const UPDATE_COMMAND_OPTIONS = 'UPDATE_COMMAND_OPTIONS;';

const defaultCommand = {
  enabled: true,
  display: {},
  options: {},

  isEnabled: (store, command) => command.enabled,
  isVisible: (store, command) => command.display,

  command: null,
  widget: null,
};

function makeCommand(state, command) {
  return state.set(command.name, command);
}

function newInitialState() {
  return Map({
    all: {},
    allCommands: List(),
    tools: List(),
    activeCommand: null,
  });
}

function initCommand(attrs, name) {
  const cmd = Object.assign({}, defaultCommand, attrs, { name });
  if (cmd.display === true) cmd.display = { playerTools: true };
  return cmd;
}

const reducer = createReducer({}, {
  [ActionTypes.INIT](state) {
    return newInitialState();
  },

  [ADD_COMMAND](state, { command, name }) {
    const cmd = initCommand(command, name);
    const commands = state.get('all');
    commands[name] = cmd;
    let newState = state.updateIn(['allCommands'], List(), list => list.push(name));
    if (cmd.tool) newState = newState.updateIn(['tools'], List(), list => list.push(name));
    return newState;
  },

  [REMOVE_COMMAND](state, commandName) {
    const commands = state.get('all');
    delete commands[commandName];
    const idx = state.getIn(['tools']).indexOf(commandName);
    const idx2 = state.getIn(['allCommands']).indexOf(commandName);
    return state.deleteIn(['tools', idx]).deleteIn(['allCommands', idx2]);
    // FIXME: remove from commands/widgets
  },
  // [RUN_COMMAND](state, commandName) {
  //   return state.setIn(['commands', commandName, 'running'], true);
  // },
  // [FINISH_COMMAND](state, commandName) {
  //   return state.setIn(['commands', commandName, 'running'], false);
  // },
  // [FAIL_COMMAND](state, commandName) {
  //   return state.setIn(['commands', commandName, 'running'], false);
  // },
  [UPDATE_COMMAND](state, { name, opts }) {
    const command = state.get('all')[name];
    if (command) Object.assign(command, opts);
    return state;
  },

  [UPDATE_COMMAND_OPTIONS](state, { name, opts }) {
    const command = state.get('all')[name];
    if (command) Object.assign(command.options, opts);
    return state;
  },
});

/**
 * Add a command
 *
 * @public
 * @param {Command} command Command to be added
 * @param {String} name provide a name for the tool to allow removing it.
 * @returns {Promise}
 *
 */
export function addCommand(command, name) {
  return { type: ADD_COMMAND, payload: { command, name } };
}

/**
 * Remove a command
 *
 * @public
 * @param {String} name The name of the command to remove
 * @returns {Promise}
 */
export function removeCommand(commandName) {
  return { type: REMOVE_COMMAND, payload: commandName };
}

/**
 * Updates Atributes of Command
 *
 * @public
 * @param {String} name The name of the command to be updated
 * @param {Object} opts Attributes to set in the command
 * @returns {Promise}
 *
 */
export function updateCommand(name, opts) {
  return { type: UPDATE_COMMAND, payload: { name, opts } };
}

/**
 * Updates Options object of Command
 *
 * @public
 * @param {String} name The name of the command to be updated
 * @param {Object} opts Options to be set in the command
 * @returns {Promise}
 *
 */
export function setCommandOptions(name, opts) {
  return { type: UPDATE_COMMAND_OPTIONS, payload: { name, opts } };
}

export function getTools(store) {
  return store.getIn(['commands', 'tools']);
}

export function getCommands(store, displayLocation) {
  const commands = store.getIn(['commands', 'all']);
  return !displayLocation ? commands : reduce((cmds, key) => {
    const cmd = commands[key];
    if (cmd.enabled && cmd.display[displayLocation]) cmds[key] = cmd;
    return cmds;
  }, {}, Object.keys(commands));
}

function _isCommandActive(store, command) {
  if (command.active) return true;
  if (command.isActive) return command.isActive(store);
  return false;
}

export function isCommandActive(store, commandName) {
  const command = store.getIn(['commands', 'all'])[commandName];
  return command && _isCommandActive(store, command);
}

export function getActiveCommandsWithWidgets(store) {
  const commands = getCommands(store);
  const result = {};
  Object.values(commands).forEach((command) => {
    if (command.widget && _isCommandActive(store, command)) result[command.name] = command;
  });
  return result;
}

export function activateCommand(commandName) {
  return (store) => {
    const tools = getTools(store);
    const commands = getCommands(store);
    const command = commands[commandName];

    if (command.activate) {
      if (!_isCommandActive(store, command)) {
        command.activate.call(command);
        store.dispatch(updateCommand(commandName, { active: true }));
      }
    } else {
      tools.forEach((toolName) => {
        const command = commands[toolName];
        if (command.active && command.name !== commandName) {
          store.dispatch(updateCommand(command.name, { active: false }));
        }

        if (command.name === commandName) {
          store.dispatch(updateCommand(command.name, { active: true }));
        }
      });
    }
  };
}

export function deactivateCommand(commandName) {
  return (store) => {
    const commands = getCommands(store);
    const command = commands[commandName];
    if (!command) return;

    if (command.active) store.dispatch(updateCommand(command.name, { active: false }));
    if (command.deactivate) command.deactivate.call(command);
  };
}

/**
 * Executes, Activates/Deactivates, or makes Command Active
 *
 * @public
 * @param {String} commandName The name of the command to be run
 * @returns {Promise}
 *
 */
export function runCommand(commandName) {
  return (store) => {
    const command = store.getIn(['commands', 'all'])[commandName];
    if (!command || !command.enabled) return;

    if (command.execute) return command.execute(store);
    const isActive =  _isCommandActive(store, command);
    if (command.deactivate && isActive) return store.dispatch(deactivateCommand(commandName));
    return store.dispatch(activateCommand(commandName));
  };
}

/**
 * Updates multiple commands with given Attributes
 *
 * @public
 * @param {String} commandList The name of the command to be run
 * @params {Object} attrs Attributes to be set in the command
 * @returns {Promise}
 *
 */
export function updateCommands(commandList, attrs) {
  return (store) => {
    commandList.forEach((name) => {
      store.dispatch(updateCommand(name, attrs));
    });
  };
}

/**
 * Return a Map with the command commands.
 *
 * @private
 */
// export function getcommands(store) {
//   return store.get(['commands']).map((command) => command.get('command'));
// }

const publicApi = {
  reducer,
  actions: { activateCommand, addCommand, deactivateCommand, runCommand, setCommandOptions, updateCommand, updateCommands, removeCommand },
  selectors: { },
};

export default publicApi;



// WEBPACK FOOTER //
// ./src/modules/commands.js